import { FeatureBlock, IntegrationConfig } from './config.model';
import { migrateIntegrationToV2 } from './integration-migration';

/**
 * Pure operations behind the Shopper chat settings page.
 *
 * React-free on purpose, in the same spirit as `integration-migration.ts`:
 * importing the page itself drags Auth, lottie and react-leaflet through the
 * module graph, and a test that only wants to know what a save writes should
 * not have to stand any of that up.
 */

/**
 * Merge a patch into `integration.features.chat`, leaving everything else alone.
 *
 * Patching rather than replacing is the point: a vendor can have the chat page
 * and the Integration page open at once, and a save from the chat page must not
 * drop the domains, matching rules or other features configured over there.
 */
export const applyChatPatch = (
    integration: IntegrationConfig,
    patch: Partial<FeatureBlock>,
): IntegrationConfig => {
    /*
     * Migrate a legacy (v1) config before adding the chat to it.
     *
     * A save from this page marks the config schemaVersion 2, and a v2 config
     * is read from its features map alone. Adding `chat` to a v1 config without
     * migrating it produced a v2 config whose only feature was the chat: the
     * vendor's visualizer button — enabled flag, design, open mode, all kept in
     * the legacy top-level fields — was no longer read by the SDK, and the
     * button disappeared from their site. Observed on a production vendor.
     * The migration seeds `features.visualizer` from those legacy fields.
     */
    const migrated = migrateIntegrationToV2(integration);
    const features = { ...(migrated.features || {}) } as Record<string, FeatureBlock>;
    const existingChat = integration.features?.chat || features.chat;
    features.chat = { ...(existingChat || ({ enabled: false } as FeatureBlock)), ...patch };
    return { ...migrated, features } as IntegrationConfig;
};

/**
 * The integration as it should be persisted from the chat page.
 *
 * `isEnabled` is the backend's master gate on /config — it 404s the whole
 * integration when nothing is enabled — and it tracks ANY feature, so a vendor
 * whose only enabled feature is the chat still gets a config served. The
 * Integration page derives it the same way.
 */
export const prepareChatSave = (integration: IntegrationConfig): IntegrationConfig => ({
    ...integration,
    schemaVersion: 2,
    isEnabled: Object.values(integration.features || {}).some((f: any) => !!f?.enabled),
});

/** The SDK's final fallback when a vendor has no brand colour. */
export const LAUNCHER_DEFAULT_BACKGROUND = '#0052cc';

const HEX = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;

/**
 * Black-ish or white text, whichever reads on `hex`.
 *
 * The same rule as `readableOn` in ardealy-sdk-v2.js — kept identical so the
 * preview on this page is what shoppers actually see.
 */
export const readableOn = (hex: string): string => {
    const match = HEX.exec(String(hex || '').trim());
    if (!match) return '#FFFFFF';
    const [r, g, b] = [1, 2, 3].map((i) => {
        const c = parseInt(match[i], 16) / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.45 ? '#1A1A2E' : '#FFFFFF';
};

/** `hex` darkened by `amount` (0–1). The SDK's automatic hover background. */
export const darken = (hex: string, amount = 0.08): string => {
    const match = HEX.exec(String(hex || '').trim());
    if (!match) return hex;
    return '#' + [1, 2, 3]
        .map((i) => Math.round(parseInt(match[i], 16) * (1 - amount)).toString(16).padStart(2, '0'))
        .join('');
};

/**
 * The four launcher colours, with every empty setting filled the way the SDK
 * fills it: background from the brand's primary colour, hover background from
 * its secondary colour (a shade darker than the background when there is none),
 * and each text colour from contrast with its background.
 */
export const resolveLauncherColors = (
    settings: { color?: string; textColor?: string; hoverColor?: string; hoverTextColor?: string },
    brandColor?: string,
    brandSecondaryColor?: string,
) => {
    const background = settings.color || brandColor || LAUNCHER_DEFAULT_BACKGROUND;
    const text = settings.textColor || readableOn(background);
    const hoverBackground = settings.hoverColor || brandSecondaryColor || darken(background);
    const hoverText = settings.hoverTextColor
        || (settings.hoverColor || brandSecondaryColor ? readableOn(hoverBackground) : text);
    return { background, text, hoverBackground, hoverText };
};

/** What the returning-visitor bubble says for a product: the vendor's wording or the default, with `{product}` filled in. */
export const returningNudgeText = (message: string | undefined, fallback: string, productName: string): string =>
    ((message || '').trim() || fallback).split('{product}').join(productName);

/**
 * When a returning shopper is invited, as four numbers.
 *
 * The choices offered are a subset of what the API and SDK accept
 * (returning-visitors.ts / returningNudgeSettings), so anything chosen here is
 * enforced as shown.
 */
export interface ReturningRule {
    /** Views of the same product. */
    minVisits: number;
    /** On at least this many different days. */
    minDays: number;
    /** Counting only views from the last this-many days. */
    windowDays: number;
    /** Then the same product stays quiet this long. */
    cooldownDays: number;
}

export const RETURNING_RULE_DEFAULTS: ReturningRule = { minVisits: 3, minDays: 2, windowDays: 14, cooldownDays: 7 };
export const RETURNING_VISIT_CHOICES = [2, 3, 4, 5, 6] as const;
export const RETURNING_DAY_CHOICES = [1, 2, 3, 4, 5] as const;
export const RETURNING_WINDOW_CHOICES = [7, 14, 30] as const;
export const RETURNING_COOLDOWN_CHOICES = [3, 7, 14, 30] as const;

const pick = (value: unknown, choices: readonly number[], fallback: number) =>
    choices.includes(Number(value)) ? Number(value) : fallback;

/** The stored values as the four numbers the page shows; anything unknown falls back to the default. */
export const returningRule = (stored: Partial<ReturningRule> | undefined): ReturningRule => {
    const d = RETURNING_RULE_DEFAULTS;
    const minVisits = pick(stored?.minVisits, RETURNING_VISIT_CHOICES, d.minVisits);
    return {
        minVisits,
        // Never more different days than views: "3 views on 4 days" cannot happen.
        minDays: Math.min(minVisits, pick(stored?.minDays, RETURNING_DAY_CHOICES, d.minDays)),
        windowDays: pick(stored?.windowDays, RETURNING_WINDOW_CHOICES, d.windowDays),
        cooldownDays: pick(stored?.cooldownDays, RETURNING_COOLDOWN_CHOICES, d.cooldownDays),
    };
};

/**
 * One change to the rule, keeping it possible.
 *
 * Asking for more different days than views raises the views to match, and
 * lowering the views below the days lowers the days — the number just chosen
 * is the one kept.
 */
export const patchReturningRule = (current: Partial<ReturningRule> | undefined, patch: Partial<ReturningRule>): ReturningRule => {
    const next = { ...returningRule(current), ...patch };
    if (patch.minDays !== undefined && next.minDays > next.minVisits) next.minVisits = next.minDays;
    if (patch.minVisits !== undefined && next.minDays > next.minVisits) next.minDays = next.minVisits;
    return next;
};

/** The consent tools the SDK reads, by the name a vendor knows them by. */
export const CONSENT_SOURCE_NAMES: Record<string, string> = {
    tcf: 'IAB TCF',
    cookiebot: 'Cookiebot',
    onetrust: 'OneTrust',
    didomi: 'Didomi',
    axeptio: 'Axeptio',
    shopify: 'Shopify',
    google_consent_mode: 'Google Consent Mode',
};

/**
 * What the settings page says about consent on the vendor's site, as a status
 * and the tool's name: `detected` (a tool the SDK reads), `fallback` (only our
 * own banner or setConsent answered), `missing` (no banner it can read — the
 * nudge will not show in auto mode), `waiting` (nothing reported yet).
 */
export const consentStatus = (
    seen: { source: string | null } | null | undefined,
): { status: 'detected' | 'fallback' | 'missing' | 'waiting'; name?: string } => {
    const source = seen?.source;
    if (!source) return { status: 'waiting' };
    if (CONSENT_SOURCE_NAMES[source]) return { status: 'detected', name: CONSENT_SOURCE_NAMES[source] };
    if (source === 'none') return { status: 'missing' };
    return { status: 'fallback' };
};
